import React, { useEffect, useEffectEvent, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation } from '@apollo/client'
import { bool, func, shape, string } from 'prop-types'
import { QRCodeCanvas } from 'qrcode.react'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import ImageButtonTile, { IMAGE_BUTTON_TILE_COLOR_NEUTRAL } from '@divvy-web/skylab.imagebuttontile'
import Modal from '@divvy-web/skylab.modal'
import Spinner from '@divvy-web/skylab.spinner'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import ImageWrapper from '@sputnik/components/ImageWrapper'
import GenerateRequestedDocUploadUrl from '@sputnik/components/UploaderTileList/GenerateRequestedDocUploadUrl.gql'
import { uploadToS3 } from '@sputnik/components/UploaderTileList/s3Uploader'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { documentUploadTypeModalStyles } from './documentUploadTypeModalStyles'

const DocumentUploadTypeModal = ({ appId, isShowing, selectedDocument, setIsModalShowing, token }) => {
  const [isUploading, setIsUploading] = useState(false)
  const onNotShowing = useEffectEvent(() => {
    setIsUploading(false)
  })
  const makeTestId = useMakeTestId('DocumentUploadTypeModal')
  const showDangerToast = useToast(TOAST_TYPE_DANGER)
  const hostName = window.location.hostname
  const { selectedDocumentType: documentType, selectedDocumentUuid: documentUuid } = selectedDocument

  useEffect(() => {
    if (!isShowing) {
      onNotShowing()
    }
  }, [isShowing])

  const handleError = (action) => (e) => {
    logError(e, {
      attributes: {
        creditAppId: appId,
        action,
        result: e,
      },
      eventName: 'UploadDocument',
    })

    showDangerToast(
      <FormattedMessage
        defaultMessage='There was an error uploading this document'
        id='sputnik.DocumentUploadTypeModal__7NF4bS'
      />,
      { autoHideDelay: 5000 },
    )

    setIsUploading(false)
  }

  const [getSignedUrl, { loading }] = useMutation(GenerateRequestedDocUploadUrl, {
    onError: handleError('GenerateRequestedDocUploadUrl'),
  })

  const isLoading = loading || isUploading

  const onDrop = async (acceptedFiles) => {
    const selectedFile = acceptedFiles?.[0]

    if (acceptedFileTypes.length > 0) {
      setIsUploading(true)

      await getSignedUrl({
        variables: {
          documentUuid,
          fileName: selectedFile.name,
        },
      })
        .then(({ data }) => {
          const uploadUrl = data?.docUploadInfo?.url

          if (uploadUrl) {
            return uploadToS3(selectedFile, uploadUrl)
          }

          throw new Error('No Upload URL returned from the server')
        })
        .then(() => {
          logInfo('uploadToS3', {
            attributes: {
              creditAppId: appId,
              result: `Document uploaded to S3: ${documentType}`,
            },
            eventName: 'UploadDocument',
          })
        })
        .catch(handleError('UploadToS3'))
    }
  }

  const acceptedFileTypes = 'image/jpeg, application/pdf'

  const { getInputProps, open } = useDropzone({
    accept: acceptedFileTypes,
    noClick: true,
    noKeyboard: true,
    onDrop,
  })

  return (
    <Modal
      css={documentUploadTypeModalStyles}
      dataTestId={makeTestId('')}
      isShowing={isShowing}
      title={
        <FormattedMessage
          defaultMessage='How would you like to upload?'
          id='sputnik.DocumentUploadTypeModal__PCONMC'
        />
      }
      onCloseClick={() => setIsModalShowing(false)}
    >
      <div className='tiles-wrapper'>
        <input {...getInputProps()} />
        <ImageButtonTile
          className='qrcode-tile'
          color={IMAGE_BUTTON_TILE_COLOR_NEUTRAL}
          dataTestId={makeTestId('qrcode')}
          image={
            <div className='document-source-img-wrapper'>
              <div data-testid={makeTestId('qrcode-div')}>
                <QRCodeCanvas
                  imageSettings={{
                    height: '35',
                    src: `https://app.divvy.co/assets/logos/document-upload-type-modal-qrcode@3x.png`,
                    width: '35',
                  }}
                  value={`https://${hostName}/auth?referrer=mobile-doc-upload&authToken=${token}`}
                />
              </div>
            </div>
          }
          title={
            <FormattedMessage
              defaultMessage='Your mobile phone'
              id='sputnik.DocumentUploadTypeModal__hAvsjh'
            />
          }
          onClick={() => {}}
        >
          <FormattedMessage
            defaultMessage='Scan this code with your mobile phone to upload.'
            id='sputnik.DocumentUploadTypeModal__CWac43'
          />
        </ImageButtonTile>
        <ImageButtonTile
          className='your-computer-image'
          color={IMAGE_BUTTON_TILE_COLOR_NEUTRAL}
          dataTestId={makeTestId('upload-from-computer')}
          disabled={isLoading}
          image={
            isLoading ? (
              <Spinner centered />
            ) : (
              <ImageWrapper
                alt='person working on computer'
                imageName='document-upload-type-modal-computer'
              />
            )
          }
          title={
            <FormattedMessage
              defaultMessage='Your computer'
              id='sputnik.DocumentUploadTypeModal__MMpdPP'
            />
          }
          onClick={() => {
            logInfo('initiateDocumentUpload', {
              attributes: {
                creditAppId: appId,
                result: 'User initiated a document upload on the current machine',
              },
              eventName: 'SameDeviceDocumentUpload',
            })
            open()
          }}
        >
          <FormattedMessage
            defaultMessage='Select files from your computer to upload.'
            id='sputnik.DocumentUploadTypeModal__B+Q//T'
          />
        </ImageButtonTile>
      </div>
    </Modal>
  )
}

DocumentUploadTypeModal.propTypes = {
  appId: string,
  isShowing: bool,
  selectedDocument: shape({
    selectedDocumentType: string,
    selectedDocumentUuid: string,
  }),
  setIsModalShowing: func,
  token: string,
}

export default DocumentUploadTypeModal
